<script setup lang="ts">
import { computed, inject, nextTick, onMounted, ref, useTemplateRef, watch } from 'vue';
import type { Placement } from '@/modules/common/types/placement.ts';
import { SECTION_TABLE_SEARCH_QUERY_KEY } from '@/modules/core/consts/section-table-search.const.ts';
import { splitSectionTableSearchHighlight } from '@/modules/core/utils/section-table-search-highlight.util.ts';
import UiTooltip from '@/modules/ui/components/ui/UiTooltip.vue';

const { text, placement = 'bottom' } = defineProps<{
  text: string;
  placement?: Placement;
}>();

const searchQuery = inject(SECTION_TABLE_SEARCH_QUERY_KEY, null);
const contentRef = useTemplateRef('content');
const disabled = ref(true);

const parts = computed(() => splitSectionTableSearchHighlight(text, searchQuery?.value?.trim() ?? ''));

function updateDisabled() {
  const el = contentRef.value;
  disabled.value = !el || el.scrollWidth <= el.clientWidth;
}

onMounted(() => {
  void nextTick(updateDisabled);
});

watch(
  () => [text, searchQuery?.value] as const,
  () => {
    void nextTick(updateDisabled);
  }
);
</script>

<template>
  <UiTooltip
    class="section-table-highlight-text"
    :text="text"
    :placement="placement"
    :disabled="disabled"
    @mouseenter="updateDisabled"
  >
    <span
      ref="content"
      class="section-table-highlight-text__content"
    >
      <template
        v-for="(part, index) in parts"
        :key="index"
      >
        <b
          v-if="part.match"
          class="section-table-highlight-text__match"
          >{{ part.text }}</b
        >
        <template v-else>{{ part.text }}</template>
      </template>
    </span>
  </UiTooltip>
</template>

<style scoped lang="scss">
.section-table-highlight-text {
  display: block;
  min-width: 0;
  max-width: 100%;
}

.section-table-highlight-text__content {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section-table-highlight-text__match {
  font-weight: var(--font-weight-extra-bold);
}
</style>
